import { defineStore } from "pinia"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { useViewFamilyStore } from "@/rd/ViewFamilyStore"
import { DateTime } from "luxon"
import { useResearchDataNotifications } from "@/rd/ResearchDataNotifications"
import { ValueCount, ViewPerson } from "@/rd/ResearchDataModel"
import { useSyncStore } from "@/rd/SyncStore"
import { AsyncData, LoadMode } from "@/util/AsyncData"
import { useWorkspaceStore } from "@/rd/WorkspaceStore"
import { useDataGroupStore } from "@/gp/DataGroupStore"
import { AsyncDataStore } from "@/util/AsyncDataStore"
import { CompositeId } from "@/rd/CompositeId"
import { useGroupAdminNotifications } from "@/gp/GroupAdminNotifications"
import { chunk } from "lodash"

export class ViewScopeInfo {
    startPersonId = ''
    loadedDate = DateTime.utc()
    personIds = new Set<string>()
    groupIds = new Set<string>()
}

export class ScopeLoadProgress {
    ancestors = 0
    descendants = 0
    totalPersons = 0
}

export const useViewScopeStore = defineStore('viewScopeStore', () => {
    const asyncScopeInfos = new Map<string, AsyncData<ViewScopeInfo>>()
    const loadProgress = new Map<string, ScopeLoadProgress>()

    const maxAncestorGenerations = 100 // sanity limit
    const downGenerations = 2 // siblings and first cousins of direct ancestors
    const maxDescendantGenerations = 20 // sanity limit

    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()
    const gpNotify = useGroupAdminNotifications()
    const rdNotify = useResearchDataNotifications()
    const syncStore = useSyncStore()
    const workspaceStore = useWorkspaceStore()
    const dataGroupStore = useDataGroupStore()

    gpNotify.onDataGroupMerged(async args => {
        const store = useViewScopeStore() // use to get/return reactive store objects
        for (const asi of store.asyncScopeInfos.values()) {
            if (asi.data) {
                const fromGroupInScope = asi.data.groupIds.has(args.groupId)
                const intoGroupInScope = asi.data.groupIds.has(args.intoGroupId)
                if (fromGroupInScope != intoGroupInScope) {
                    console.log('Invalidating view scope due to group merge')
                    AsyncData.invalidate(asi) // one in scope, other not
                }
            }
        }

        const intoGroup = await dataGroupStore.getAsyncGroup(args.intoGroupId)?.whenLoadCompleted
        if (intoGroup) {
            console.log('Invalidating merge into group relationships')
            AsyncData.invalidate(viewPersonStore.asyncParentFamilyKeys.get(intoGroup.startItemId!))
            AsyncData.invalidate(viewPersonStore.asyncSpouseFamilyKeys.get(intoGroup.startItemId!))
        }
    })
    
    //rdNotify.onGroupDataChanged(async args => {
    //}

    // RG sync 
    //  -> RG data modified event (single)
    //      -> ... -> RG auto match find changes
    //          -<≡ RG auto match (person batch)
    //              ≡>- RG auto match completed
    //  -<≡ update tracking PGs (PG batch)
    //      ≡>- sync completed event
    //      -> PG data modified event (PG batch)
    //          -> ... -> PG auto match find changes (PG batch)
    //              ≡>-<≡ PG auto match (person batch) 
    //                  ≡>- PG auto match completed

    // PG edit
    //   -> PG data modified event (single)
    //      -> ... -> PG auto match find changes (single)
    //          -> PG auto match (single) 
    //              -> PG auto match completed

    // RG added - don't care (initially empty)
    // PG added (many) - don't care (initially empty)
    // RG sync completed - don't care (RG + TPGs completed, not in scope until match or merge)
    // PG merge - !!! if merge-to in scope (common), check for "RG connect"
    // PG matching completed (many) - !!! if TPGs from overlapping RGs, could be a mess until matched
    // RG matching completed - ??? could detect RG overlap with new API call

    // matching completed is only important if it follows a newly added RG
    // - to RG owner (likely only user RG is visible to), chance of overlap in scope is moderate (why?)
    // - if PG, not likely created as 

    // PG/rel change/merge should only trigger reload if change moves RG in/out of scope
    // moving a few PGs in/out has negligible impact on summary consumers, and detail consumers will respond to cache invalidation
    // only possible if RG out of scope (e.g. no living in tree, adding and connecting to RG manually)
    // living in scope is max 4 generations from start person
    // can detect "RG connected" event by traversing living scope (start +/- 4) and checking for RG not in current scope
    // should trigger detection on new RG 

    // on owned RG sync complete, mark scope as stale (flag sync as containing major changes)
    // /removed or FG mem added/removed, 
    // on RG data change for RG not in scope (new!), wait for async processing to complete?

    // RG added, but nothing in it yet
    // Sync completed (incl. linked PGs), but if new, no merge or matching yet (RG not connected to self)
    // Merge completed (no event!)
    // Matching completed (no event!), but may take a while and have no effect

    // new : UPG
    // RG 1: UPG + 1 RG + 20 PG + merge
    // RG 2: UPG + 2 RG + 40 PG + merge(?)

    // group is now stale -- does anyone care? don't reload if not needed
    // can tell cached item is stale if AsyncData<T>.asOfDate < group.modifiedDate for item's group
    // ensureLoaded will do nothing unless stale VPs and rels can be detected

    // scope info describes scope constraints and when/if it was loaded (completely!)
    // load date should be reliable indicator that ENTIRE scope is loaded (shouldn't have to walk the tree to verify)
    // if cache is cleared, clear load date on all scopes, keep last known details (might be useful?)
    // if cache is pruned but current scope preserved, no change
    // if cache is pruned and scope is affected, treat same as full cache clear (scope state unknown)

    async function reload() {
        // do something
    }

    function getAsyncScopeInfo(startPersonId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useViewScopeStore() // use to get/return reactive store objects
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            if (ids.length != 1)
                throw new Error(`getAsyncScopeInfo: expected 1 id, got ${ids.length}`)

            let start = performance.now()

            // load ancestors and descendants in parallel
            store.loadProgress.set(ids[0], new ScopeLoadProgress())
            const loadAncestors = ensureAncestorsLoadedAsync(ids[0], true)
            const loadDescendants = ensureDescendantsLoadedAsync(ids[0], true)
            const directAncestorIds = await loadAncestors
            const descendantIds = await loadDescendants

            console.log(`ViewScope: loaded ${directAncestorIds.size} direct ancestors and ${descendantIds.size} descendants in ${(performance.now() - start).toFixed()} ms`)

            const scopePersons = new Map([...descendantIds].map(id => [id, viewPersonStore.getLoadedPerson(id)!]))
            start = performance.now()
            for (const vp of viewPersonStore.getLoadedAncestors(ids[0], maxAncestorGenerations, downGenerations)) {
                scopePersons.set(vp.id!, vp)
            }

            console.log(`ViewScope: found ${scopePersons.size} scope persons total`)
            updateProgress(ids[0], { totalPersons: scopePersons.size })

            const newScopeInfo = new ViewScopeInfo()
            newScopeInfo.startPersonId = ids[0]
            newScopeInfo.loadedDate = requestTime
            newScopeInfo.personIds = new Set(scopePersons.keys())
            newScopeInfo.groupIds = getGroupIds(scopePersons.values())
            return new Map([[ids[0], newScopeInfo]])
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncScopeInfos, [startPersonId], 'ViewScopeInfo', loadMode, loadAsync)[0]
    }

    async function ensureAncestorsLoadedAsync(startPersonId: string, reload = false) {
        const directAncestorIds = new Set<string>()
        const needAncestorsIds = new Set([startPersonId!])
        const maxChunkGenerations = 4
        let generation = 0
        while (needAncestorsIds.size && generation < maxAncestorGenerations) {
            const oldAncestorIds = [...needAncestorsIds]
            needAncestorsIds.clear()
            const chunkGenerations = Math.min(maxChunkGenerations, maxAncestorGenerations - generation)

            console.log(`ViewScope: loading ${chunkGenerations} ancestor generations for ${oldAncestorIds.length} persons at depth ${generation}`)
            for (const ids of chunk(oldAncestorIds, 20)) {
                const start = performance.now()
                await viewPersonStore.ensureAncestorsLoadedAsync(ids, chunkGenerations, downGenerations, reload)

                const prevAncestorCount = directAncestorIds.size
                for (const id of ids) {
                    for (const vp of viewPersonStore.getLoadedAncestors(id, chunkGenerations, 0)) {
                        directAncestorIds.add(vp.id!) // may be duplicate

                        // continue loading if needed
                        const asyncFamKeyList = viewPersonStore.getAsyncParentFamiliesKeyList(vp.id)?.data
                        if (!asyncFamKeyList?.isComplete) {
                            needAncestorsIds.add(vp.id!)
                        }
                    }
                }

                const newAncestorCount = directAncestorIds.size - prevAncestorCount
                const duration = (performance.now() - start).toFixed()
                console.log(`ViewScope: loaded ${newAncestorCount} direct ancestors for ${ids.length} persons in ${duration} ms`)
                updateProgress(startPersonId, { ancestors: directAncestorIds.size })
            }
            generation += chunkGenerations
        }
        return directAncestorIds
    }

    async function ensureDescendantsLoadedAsync(startPersonId: string, reload = false) {
        const descendantIds = new Set<string>()
        const needDescendantsIds = new Set([startPersonId])
        const maxChunkGenerations = 4
        let generation = 0
        while (needDescendantsIds.size && generation < maxDescendantGenerations) {
            const oldDescendantIds = [...needDescendantsIds]
            needDescendantsIds.clear()
            const chunkGenerations = Math.min(maxChunkGenerations, maxDescendantGenerations - generation)

            console.log(`ViewScope: loading ${chunkGenerations} descendant generations for ${oldDescendantIds.length} persons at depth ${generation}`)
            for (const id of oldDescendantIds) {
                await viewPersonStore.ensureDescendantsLoadedAsync(id, chunkGenerations, reload)
                const prevDescendantCount = descendantIds.size
                for (const vp of viewPersonStore.getLoadedDescendants(id, chunkGenerations)) {
                    descendantIds.add(vp.id!) // may be duplicate
                    const asyncFamKeyList = viewPersonStore.getAsyncSpouseFamiliesKeyList(vp.id)?.data
                    if (!asyncFamKeyList?.isComplete) {
                        needDescendantsIds.add(vp.id!)
                    }
                }

                const newDescendantCount = descendantIds.size - prevDescendantCount
                if (newDescendantCount > 0) {
                    console.log(`ViewScope: loaded ${newDescendantCount} new descendants for person ${id}`)
                    updateProgress(startPersonId, { descendants: descendantIds.size })
                }
            }
            generation += chunkGenerations
        }
        return descendantIds
    }

    function getGroupIds(vps: Iterable<ViewPerson>) {
        // this function is optimized to avoid string allocations via getGroupId (use hasGroupId 99% of the time)
        // it assumes large scopes are dominated by a few research groups, so an MRU list will have a high early hit rate

        const groupIds = new Set<string>()
        const mruGroups: ValueCount[] = []
        const maxMruGroups = 10 // max isn't critical, since MRU bubble up and dominance of RGs over PGs means deep searches should be rare

        for (const vp of vps) {
            for (let m = 0; m < vp.matchIds.length; m++) {
                const personId = vp.matchIds[m]
                let found = false
                for (let i = 0; i < mruGroups.length; i++) {
                    if (CompositeId.hasGroupId(vp.id!, mruGroups[i].value)) {
                        mruGroups[i].count++
                        found = true

                        // bubble up if needed
                        if (i > 0 && mruGroups[i].count > mruGroups[i - 1].count) {
                            const temp = mruGroups[i]
                            mruGroups[i] = mruGroups[i - 1]
                            mruGroups[i - 1] = temp
                        }
                        break
                    }
                }
                if (!found) {
                    const groupId = CompositeId.getGroupId(vp.id!)
                    if (groupId) {
                        groupIds.add(groupId) // track unique

                        // append/replace end of MRU list
                        if (mruGroups.length == maxMruGroups) {
                            mruGroups.pop()
                        }
                        mruGroups.push({ value: groupId, count: 1 })
                    }
                }
            }
        }
        return groupIds
    }

    function updateProgress(id: string, updates: Partial<ScopeLoadProgress>) {
        const store = useViewScopeStore()
        const prev = store.loadProgress.get(id)
        if (!prev) return
        const next = { ...prev, ...updates }
        if (!('totalPersons' in updates))
            next.totalPersons = next.ancestors + next.descendants
        store.loadProgress.set(id, next)
    }

    // PT summary consumers want:
    // - full PT pre-loaded if possible
    // - enumerate vps in PT (no duplicates)
    // - event on major PT count change (-> refresh summary)
    
    // PT detail consumers want:
    // - near PT pre-loaded if possible
    // - get/load small subset on demand (include all known updates)
    // - O(1) check if vp is direct ancestor/descendant

    // TODO:
    // - load PT in chunks to keep request cost low, predictable
    // - catch owned PG non-tracking add (ignore RG add)
    // - catch owned RG sync complete (if only visible RG)
    // - catch owned RG auto match complete (if multiple RGs visible)
    // - catch FG mem add/remove
    // - ignore non-owner FG add/remove PG/RG (show changes eventually)
    // - on add/remove RG, assume connection to PT, reload
    // - on add/remove PG or rel, ensure PT loaded (no reload), update PT count
    // - on major PT count change (>x%), refresh summary visuals (names, places, dates)
    // - reactive person page on change to displayed vps and/or rels (incl. rel path), refresh person page

    return {
        asyncScopeInfos,
        loadProgress,

        getAsyncScopeInfo,
    }
})
